import React, { useEffect } from 'react';
import { Alert, Box, Paper, SxProps, useMediaQuery, useTheme } from '@mui/material';
import * as Types from 'types/types';
import { useAuth } from 'hooks/AuthContext';
import { Scrollable } from 'components/Scrollable';
import { useLocation } from 'react-router-dom';
import { JobsDetailsPanel, JobsMobileDialog } from './JobsViewDetails';
import { JobsViewTable } from './JobsViewTable';
import { SortField, SortOrder, sortJobs } from './jobsViewUtils';

interface JobsViewProps {
  filter?: Record<string, string | number | boolean>;
  onJobSelect?: (selectedJobs: Types.Job[]) => void;
  onJobView?: (job: Types.Job) => void;
  onJobEdit?: (job: Types.Job) => void;
  onJobRegenerate?: (job: Types.Job) => Promise<Types.Job>;
  onJobDelete?: (job: Types.Job) => Promise<void>;
  onRowClick?: (job: Types.Job) => void;
  selectedJobId?: string;
  selectable?: boolean;
  showActions?: boolean;
  showDetailsPanel?: boolean;
  variant?: 'table' | 'list' | 'responsive';
  sx?: SxProps;
}

const JobsView: React.FC<JobsViewProps> = ({
  onJobSelect,
  onJobView,
  onJobEdit,
  onJobRegenerate,
  onJobDelete,
  onRowClick,
  selectedJobId,
  selectable = true,
  showActions = true,
  showDetailsPanel = true,
  filter = {},
  sx = {},
}) => {
  const theme = useTheme();
  const { apiClient, user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  const [jobs, setJobs] = React.useState<Types.Job[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState<number>(0);
  const [limit, setLimit] = React.useState<number>(25);
  const [total, setTotal] = React.useState<number>(0);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [searchTimeout, setSearchTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const [selectedJobs, setSelectedJobs] = React.useState<Set<string>>(new Set());
  const [selectedJob, setSelectedJob] = React.useState<Types.Job | null>(null);
  const [sortField, setSortField] = React.useState<SortField>('updatedAt');
  const [sortOrder, setSortOrder] = React.useState<SortOrder>('desc');
  const [mobileDialogOpen, setMobileDialogOpen] = React.useState(false);
  const [detailsPanelOpen, setDetailsPanelOpen] = React.useState(showDetailsPanel);

  if (location.pathname.indexOf('/candidate/jobs') === 0) {
    filter = { ...filter, owner_id: user?.id || '' };
  }

  const fetchJobs = React.useCallback(
    async (pageNum = 0, searchTerm = '') => {
      try {
        setLoading(true);
        setError(null);
        const paginationRequest: Partial<Types.PaginatedRequest> = {
          page: pageNum + 1,
          limit: limit,
          sortBy: sortField,
          sortOrder: sortOrder,
          filters: filter,
        };

        let paginationResponse: Types.PaginatedResponse;
        if (searchTerm.trim()) {
          paginationResponse = await apiClient.searchJobs(searchTerm);
        } else {
          paginationResponse = await apiClient.getJobs(paginationRequest);
        }

        const sortedJobs = sortJobs(paginationResponse.data, sortField, sortOrder);
        let updated = false;
        if (jobs.length) {
          if (sortedJobs.length !== jobs.length) {
            updated = true;
          } else {
            for (let i = 0; i < sortedJobs.length; i++) {
              if (sortedJobs[i].id !== jobs[i].id) {
                updated = true;
                break;
              }
            }
          }
        } else {
          updated = true;
        }
        if (updated) {
          setJobs(sortedJobs);
          setTotal(paginationResponse.total);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while fetching jobs');
        setJobs([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [limit, sortField, sortOrder, apiClient]
  );

  useEffect(() => {
    if (jobs.length > 0 && !selectedJob && detailsPanelOpen) {
      console.log('Setting selected job from fetchJobs');
      setSelectedJob(jobs[0]);
    }
  }, [jobs, selectedJob, detailsPanelOpen]);

  React.useEffect(() => {
    console.log('Fetching jobs with filter:', filter, 'searchQuery:', searchQuery);
    fetchJobs(0, searchQuery);
  }, [fetchJobs, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    console.log('Handling search change:', event.target.value);
    const value = event.target.value;
    setSearchQuery(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchJobs(0, value);
    }, 500);

    setSearchTimeout(timeout);
  };

  const handlePageChange = (event: unknown, newPage: number): void => {
    console.log('Handling page change:', newPage);
    setPage(newPage);
    fetchJobs(newPage, searchQuery);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    console.log('Handling rows per page change:', event.target.value);
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    fetchJobs(0, searchQuery);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      const newSelected = new Set<string>(jobs.map(job => job.id || ''));
      setSelectedJobs(newSelected);
      onJobSelect?.(jobs);
    } else {
      setSelectedJobs(new Set());
      onJobSelect?.([]);
    }
  };

  const handleSelectJob = (jobId: string): void => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);

    const selectedJobsList = jobs.filter(job => newSelected.has(job.id || ''));
    onJobSelect?.(selectedJobsList);
  };

  const handleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleJobRowClick = (job: Types.Job): void => {
    setSelectedJob(job);
    onRowClick?.(job);
  };

  const handleViewJob = (job: Types.Job): void => {
    setSelectedJob(job);
    if (isMobile) {
      setMobileDialogOpen(true);
    } else {
      setDetailsPanelOpen(true);
    }
    onJobView?.(job);
  };

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        position: 'relative',
        minWidth: 0,
        ...sx,
      }}
    >
      <Scrollable
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <JobsViewTable
            jobs={jobs}
            loading={loading}
            total={total}
            page={page}
            limit={limit}
            searchQuery={searchQuery}
            sortField={sortField}
            sortOrder={sortOrder}
            selectedJobs={selectedJobs}
            selectedJob={selectedJob}
            selectedJobId={selectedJobId}
            selectable={selectable}
            showActions={showActions}
            isMobile={isMobile}
            isSmall={isSmall}
            onSearchChange={handleSearchChange}
            onSort={handleSort}
            onSortSelect={(field, order) => {
              setSortField(field);
              setSortOrder(order);
            }}
            onSelectAll={handleSelectAll}
            onSelectJob={handleSelectJob}
            onJobRowClick={handleJobRowClick}
            onViewJob={handleViewJob}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onJobEdit={onJobEdit}
            onJobRegenerate={onJobRegenerate}
            onJobDelete={onJobDelete}
            setJobs={setJobs}
            fetchJobs={fetchJobs}
          />
        </Paper>
      </Scrollable>

      <JobsDetailsPanel
        open={detailsPanelOpen && !isMobile}
        selectedJob={selectedJob}
        onClose={() => {
          setDetailsPanelOpen(false);
          setSelectedJob(null);
        }}
      />
      <JobsMobileDialog
        open={mobileDialogOpen}
        selectedJob={selectedJob}
        onClose={() => setMobileDialogOpen(false)}
      />
    </Box>
  );
};

export { JobsView };
